import { HeaderPage } from "@/components/HeaderPage";
import { Loading } from "@/components/Loading";
import { TextArea } from "@/components/TextArea";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useGetSectorById } from "@/hooks/useSectors";
import { useGetRegionals } from "@/hooks/useRegionals";
import { formatPhone } from "@/lib/utils";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import type { Regional } from "@/types/Regional";

export const ViewSector = () => {
  const { id } = useParams<{ id: string }>();
  const { data: sector, isLoading } = useGetSectorById(id!);
  const { data: regionalsData } = useGetRegionals(id!);

  if (isLoading) {
    return <Loading label="Carregando setor..." />;
  }

  if (!sector) {
    return (
      <div>
        <HeaderPage
          title="Visualizar setor"
          description="Informações detalhadas do setor selecionado"
        />
        <div className="flex-1 overflow-y-auto mt-10">
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <p className="text-center text-gray-500">Setor não encontrado</p>
            </CardContentUI>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderPage
        title="Visualizar setor"
        description="Informações detalhadas do setor selecionado"
      />
      <div className="flex-1 overflow-y-auto mt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Tabs defaultValue="dados" className="space-y-4">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="dados">Dados do Setor</TabsTrigger>
              <TabsTrigger value="regionais">Regionais</TabsTrigger>
            </TabsList>

            {/* Aba: Dados do Setor */}
            <TabsContent value="dados">
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <form className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informações Básicas
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Nome do Setor"
                          isRequired
                          disabled
                          value={sector.name}
                          placeholder="Ex: Secretaria de Obras"
                        />

                        <Input
                          title="Sigla"
                          isRequired
                          disabled
                          value={sector.acronym}
                          placeholder="Ex: SEMOB"
                        />
                      </div>

                      <div className="mt-4">
                        <TextArea
                          title="Descrição"
                          isRequired
                          disabled
                          value={sector.description}
                          placeholder="Descreva as responsabilidades do setor"
                        />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Informações de Contato
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Email"
                          disabled
                          isRequired
                          type="email"
                          value={sector.email}
                          placeholder="setor@municipio.gov.br"
                        />

                        <Input
                          title="Telefone"
                          disabled
                          isRequired
                          value={formatPhone(sector.phone)}
                          placeholder="(11) 1234-5678"
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Horário de Funcionamento
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          title="Horário de Início"
                          isRequired
                          type="time"
                          disabled
                          value={sector.workingHoursStart}
                        />

                        <Input
                          title="Horário de Término"
                          isRequired
                          type="time"
                          disabled
                          value={sector.workingHoursEnd}
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Configurações
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select disabled defaultValue={sector.status}>
                          <SelectTrigger title="Status" isRequired>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACTIVE">Ativo</SelectItem>
                            <SelectItem value="INACTIVE">Inativo</SelectItem>
                          </SelectContent>
                        </Select>

                        {/* <Select
                      disabled
                      defaultValue={sector.hasRegionals ? "true" : "false"}
                    >
                      <SelectTrigger title="Possui Regionais">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">Não</SelectItem>
                        <SelectItem value="true">Sim</SelectItem>
                      </SelectContent>
                    </Select> */}
                      </div>
                    </div>
                  </form>
                </CardContentUI>
              </Card>
            </TabsContent>

            {/* Aba: Regionais */}
            <TabsContent value="regionais">
              <Card className="bg-white">
                <CardContentUI className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold">
                        Regionais do Setor
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Lista de regionais cadastradas para este setor
                      </p>
                    </div>

                    <div className="space-y-3">
                      {!regionalsData || regionalsData.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Nenhuma regional cadastrada</p>
                        </div>
                      ) : (
                        regionalsData.map((regional: Regional) => (
                          <Card key={regional.id}>
                            <CardContentUI className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold">
                                      {regional.name}
                                    </h4>
                                    <Badge
                                      variant={
                                        regional.isActive
                                          ? "default"
                                          : "secondary"
                                      }
                                      className="pointer-events-none"
                                    >
                                      {regional.isActive ? "Ativa" : "Inativa"}
                                    </Badge>
                                  </div>
                                  {regional.description && (
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {regional.description}
                                    </p>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {regional.responsible && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Responsável:
                                        </span>{" "}
                                        {regional.responsible.profile
                                          ?.fullName ||
                                          regional.responsible.email}
                                      </div>
                                    )}
                                    {regional.email && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Email:
                                        </span>{" "}
                                        {regional.email}
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2">
                                    <span className="text-sm text-muted-foreground">
                                      Bairros:
                                    </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {regional.neighborhoods.map(
                                        (neighborhood, index) => (
                                          <Badge
                                            key={index}
                                            variant="outline"
                                            className="text-xs pointer-events-none"
                                          >
                                            {neighborhood}
                                          </Badge>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContentUI>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                </CardContentUI>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};
